// Profissão: tipos e rótulos.
//
// Os **dados** vêm do catálogo no banco (`profissoes`, `profissao_documentos` e
// `profissao_campos`, migration 0022) pela função `config`. Aqui ficam só o
// contrato de tipos e os rótulos de apresentação — o que a interface precisa
// saber para desenhar, e que não varia em runtime.
//
// Estava tudo em `mocks/profissoes`, junto de um array de profissões de
// mentira: importar o tipo obrigava a carregar o mock, e o mock parecia fonte
// de dado.

import type { UnidadeDePeriodo } from '@/lib/periodo';

export type TipoDocumento = 'documento' | 'texto' | 'numero' | 'data' | 'cpf' | 'cnpj';

export interface DocumentoProfissao {
  id: string;
  nome: string;
  tipo: TipoDocumento;
  obrigatorio: boolean;
}

export type TipoCampo =
  'text' | 'numero' | 'date' | 'cnpj' | 'select' | 'textarea' | 'moeda' | 'url';

export interface CampoProfissao {
  id: string;
  nome: string;
  tipo: TipoCampo;
  obrigatorio: boolean;
  placeholder?: string;
}

export type AbrangenciaTreinamento = 'nacional' | 'estadual' | 'municipal';

/**
 * Treinamento do catálogo (migration 0047).
 *
 * Deixou de pertencer a uma profissão só: `profissoes` lista a quem ele se
 * destina, e `aplicaAQualquerProfissao` cobre o que vale para todo funcionário
 * — integração, EPI — sem depender de alguém lembrar de acrescentar a lista
 * quando uma profissão nova for cadastrada.
 *
 * `vigenciaDias` continua, agora **derivada** do Período pelo banco: só leitura,
 * gravar nela não tem efeito.
 */
export interface TreinamentoProfissao {
  id: string;
  nome: string;
  norma?: string;
  urlLegislacao?: string;
  abrangencia: AbrangenciaTreinamento;
  estados?: string[];
  cidades?: string[];
  vigenciaValor: number;
  vigenciaUnidade: UnidadeDePeriodo;
  /** Derivada pelo banco a partir do Período de vigência. Só leitura. */
  vigenciaDias: number;
  prazoValor?: number | null;
  prazoUnidade?: UnidadeDePeriodo | null;
  /** Em horas. Era texto ('20h') até a migration 0047. */
  cargaHoraria?: number | null;
  obrigatorio: boolean;
  descricao?: string;
  aplicaAQualquerProfissao: boolean;
  /** Ids das profissões a que se destina. Vazio quando vale para qualquer uma. */
  profissoes: string[];
  /** Nulo: usa a janela global de `notificacao_parametros`. */
  diasAntecedenciaAviso?: number | null;
  tempoMedioGeracaoValor?: number | null;
  tempoMedioGeracaoUnidade?: UnidadeDePeriodo | null;
}

export interface Profissao {
  id: string;
  nome: string;
  descricao: string;
  ativo: boolean;
  categoria: 'operacoes' | 'seguranca' | 'manutencao' | 'limpeza' | 'administrativo' | 'outros';
  documentos: DocumentoProfissao[];
  campos: CampoProfissao[];
  treinamentos: TreinamentoProfissao[];
  observacoes?: string;
  /**
   * A profissão exige registro em conselho de classe (secao 4.1).
   *
   * Só declara a exigência. O número, a validade e o arquivo ficam na ficha do
   * funcionário, em `documentos` — guardá-los aqui seria pôr o dado de uma
   * pessoa num catálogo que todos os Tenants leem.
   */
  requerRegistroProfissional: boolean;
  /** Sigla do conselho: CREA, CRM, MTE. Só quando o registro é exigido. */
  tipoRegistroProfissional?: string | null;
}

const documentosComuns: DocumentoProfissao[] = [
  { id: 'doc-cpf', nome: 'CPF', tipo: 'texto', obrigatorio: true },
  { id: 'doc-rg', nome: 'RG', tipo: 'texto', obrigatorio: true },
  { id: 'doc-ctps', nome: 'Carteira de Trabalho (CTPS)', tipo: 'documento', obrigatorio: true },
  { id: 'doc-foto3x4', nome: 'Foto 3x4', tipo: 'documento', obrigatorio: false },
  {
    id: 'doc-comprovante',
    nome: 'Comprovante de Residência',
    tipo: 'documento',
    obrigatorio: true,
  },
  { id: 'doc-pis', nome: 'PIS/PASEP', tipo: 'texto', obrigatorio: false },
  { id: 'doc-titulo', nome: 'Título de Eleitor', tipo: 'documento', obrigatorio: false },
  { id: 'doc-cnh', nome: 'CNH', tipo: 'documento', obrigatorio: false },
];

export const tipoCampoLabels: Record<TipoCampo, string> = {
  text: 'Texto',
  numero: 'Número',
  date: 'Data',
  cnpj: 'CNPJ',
  select: 'Seleção',
  textarea: 'Texto longo',
  moeda: 'Valor (R$)',
  url: 'URL / Link',
};

export const tipoDocumentoLabels: Record<TipoDocumento, string> = {
  documento: 'Documento',
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
  cpf: 'CPF',
  cnpj: 'CNPJ',
};

export const categoriaProfissaoLabels: Record<string, string> = {
  operacoes: 'Operações',
  seguranca: 'Segurança',
  manutencao: 'Manutenção',
  limpeza: 'Limpeza',
  administrativo: 'Administrativo',
  outros: 'Outros',
};

export const categoriaProfissaoCores: Record<
  string,
  { bg: string; text: string; border: string; dot: string }
> = {
  operacoes: {
    bg: 'bg-primary-50',
    text: 'text-primary-700',
    border: 'border-primary-200',
    dot: 'bg-primary-500',
  },
  seguranca: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  manutencao: {
    bg: 'bg-accent-50',
    text: 'text-accent-700',
    border: 'border-accent-200',
    dot: 'bg-accent-500',
  },
  limpeza: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-500',
  },
  administrativo: {
    bg: 'bg-secondary-50',
    text: 'text-secondary-700',
    border: 'border-secondary-200',
    dot: 'bg-secondary-500',
  },
  outros: {
    bg: 'bg-background-100',
    text: 'text-foreground-700',
    border: 'border-background-200',
    dot: 'bg-foreground-400',
  },
};
